import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { paymentStatus, downloadManifest, API } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { Overline, PrimaryButton, GhostButton } from "@/components/site/Primitives";
import { CheckCircle2, Download } from "lucide-react";

export default function ThankYou() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("none"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setStatus("paid");
          downloadManifest(sessionId).then(setManifest).catch(() => {});
          return;
        }
        if (["expired", "failed"].includes(res.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 8) setTimeout(poll, 2000); else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  const items = manifest?.items || [];
  const isBundle = manifest?.bundle;

  return (
    <div data-testid="thank-you-page" className="min-h-[70vh] flex items-center">
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        {status === "checking" && <p className="font-serif text-3xl text-brand-muted">Confirming your order…</p>}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-brand-sage mx-auto mb-8" />
            <Overline>With gratitude</Overline>
            <h1 className="text-5xl sm:text-6xl font-light mt-4 mb-6 leading-tight">Thank you, from the cottage</h1>
            <p className="font-body text-brand-body text-lg leading-relaxed mb-6">
              Your purchase is complete{isBundle ? " — the whole Cottage Library is now yours to keep." : "."} A copy has been sent to your email.
            </p>
            {items.length > 0 ? (
              <div className="flex flex-col items-center gap-3 mb-2" data-testid="download-list">
                {isBundle && <span className="overline text-[0.62rem] mb-1">Your downloads</span>}
                {items.map((it) => (
                  <a key={it.slug} href={`${API}/download/${sessionId}?slug=${it.slug}`} data-testid={`download-${it.slug}`}
                     className="inline-flex items-center gap-2 bg-brand-olive text-brand-cream px-8 py-4 font-sans uppercase tracking-[0.14em] text-xs hover:bg-brand-charcoal transition-colors duration-500 w-full sm:w-auto justify-center">
                    <Download size={16} /> {isBundle ? it.title : "Download your book"}
                  </a>
                ))}
              </div>
            ) : (
              <p className="font-sans text-[0.72rem] uppercase tracking-[0.14em] text-brand-muted mt-2" data-testid="download-pending">
                Your download is being prepared and will arrive by email shortly.
              </p>
            )}
            <img src={IMAGES.journal} alt="Cottage table" className="w-full aspect-[16/9] object-cover my-10" />
            <p className="font-serif text-2xl italic text-brand-olive mb-10">May it bring warmth to your home. — Agnes</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryButton to="/library" testid="thankyou-library-btn">Browse more editions</PrimaryButton>
              <GhostButton to="/journal" testid="thankyou-journal-btn">Read the Journal</GhostButton>
            </div>
          </>
        )}
        {status === "pending" && <p className="font-serif text-3xl text-brand-muted">Your payment is still processing. Do check your email shortly, or contact us if you need help.</p>}
        {status === "failed" && (
          <><h1 className="text-4xl font-light mb-4">That didn't quite go through</h1>
          <p className="font-body text-brand-body text-lg mb-8">No charge was made. Do try again, or write to us if you need a hand.</p>
          <PrimaryButton to="/library" testid="thankyou-retry-btn">Back to the Library</PrimaryButton></>
        )}
        {status === "none" && (
          <><h1 className="text-4xl font-light mb-4">Welcome</h1>
          <PrimaryButton to="/library" testid="thankyou-home-btn">Enter the Cottage Library</PrimaryButton></>
        )}
      </div>
    </div>
  );
}
